import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Input,
  Space,
  Avatar,
  Tag,
  Modal,
  Form,
  Select,
  Breadcrumb,
  Card,
  Typography,
  App,
  Radio,
  Tooltip
} from 'antd';
import { 
  SearchOutlined, 
  PlusOutlined, 
  UserOutlined,
  EditOutlined,
  HomeOutlined,
  TeamOutlined,
  PhoneOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import userService from '../services/userService';
import groupService from '../services/groupService';
import businessUnitService from '../services/businessUnitService';
import PageHeader from './PageHeader';
import SearchCard from './SearchCard';
import { BREADCRUMB_PATTERNS } from './BreadcrumbNav';
import { countryCodes } from './countryCodes';

const { Option } = Select;
const { Text } = Typography;
const Team = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [buMap, setBuMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedCountryCode, setSelectedCountryCode] = useState('+27');
  const [participantFilter, setParticipantFilter] = useState('non-participants'); // 'non-participants' or 'all'
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();
  const [inviteForm] = Form.useForm();

  // Initialize form when modal opens and set default country code
  useEffect(() => {
    if (isModalVisible && !editingUser) {
      // For new user, set default country code
      form.setFieldsValue({ countryCode: '+27' });
      setSelectedCountryCode('+27');
    }
  }, [isModalVisible, editingUser, form]);

  useEffect(() => {
    loadUsers();
    loadGroups();
    loadBusinessUnits();
  }, []);

  // Reload users when filters change
  useEffect(() => {
    loadUsers();
  }, [participantFilter, searchQuery]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      // Prepare API options based on current filters
      const apiOptions = {
        search: searchQuery,
        nonParticipantsOnly: participantFilter === 'non-participants',
        participantsOnly: participantFilter === 'participants',
        limit: 100 // Get more users for local filtering
      };
      
      // Call the real API with filters
      const result = await userService.getAllUsers(apiOptions);
      
      if (result.success) {
        // Transform the data to match table format
        const transformedUsers = result.data?.data?.map((user, index) => {
          // Handle phone number display - check for new structure first, fallback to old
          let phoneDisplay = 'No phone';
          if (user?.phoneNumber) {
            if (typeof user.phoneNumber === 'object' && user.phoneNumber.full) {
              // New structure - use the full phone number
              phoneDisplay = user.phoneNumber.full;
            } else if (typeof user.phoneNumber === 'object' && user.phoneNumber.countryCode && user.phoneNumber.number) {
              // New structure without full - construct it
              phoneDisplay = `${user.phoneNumber.countryCode}${user.phoneNumber.number}`;
            } else if (typeof user.phoneNumber === 'string') {
              // Legacy structure
              phoneDisplay = user.phoneNumber;
            }
          }
          
          // Use isParticipant from backend response if available
          const isParticipant = user?.isParticipant || 
                               user?.group?.groupName?.toLowerCase().includes('participant') || 
                               user?.role?.toLowerCase().includes('participant');
          
          return {
            id: user._id || index,
            name: `${user?.name || ''} ${user?.surname || ''}`.trim() || user?.email || 'Pending User',
            email: user?.email || 'No email',
            phoneNumber: phoneDisplay,
            role: user?.group?.groupName || 'No Group',
            questionnaires: user?.group?.privileges?.length || 0,
            status: user?.status || 'active',
            businessUnit: user?.businessUnit?.name || (typeof user?.businessUnit === 'string' ? user.businessUnit : null),
            department: user?.department || null,
            avatar: null,
            rawUser: { ...user, isParticipant }
          };
        });
        setUsers(transformedUsers);

       // alert('Users loaded: ' + JSON.stringify(transformedUsers));
      } else {
        message.error('Failed to load users: ' + result.message);
        // Set empty array if API fails
        setUsers([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Failed to load users. Please check your connection.');
      // Set empty array if API fails
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const result = await groupService.getAllGroups();
     // alert('Groups loaded: ' + JSON.stringify(result));
      if (result.success) {
        setGroups(result.data || []);
      } else {
        message.error('Failed to load groups: ' + result.message);
        setGroups([]);
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      message.error('Failed to load groups.');
      setGroups([]);
    }
  };

  const loadBusinessUnits = async () => {
    try {
      const result = await businessUnitService.getAllBusinessUnits();
      if (result.success) {
        const map = {};
        (result.data || []).forEach(bu => { if (bu._id) map[bu._id] = bu.name; });
        setBuMap(map);
      }
    } catch (error) {
      console.error('Error loading business units:', error);
    }
  };

  const handleEditUser = (user) => {
    // Navigate to dedicated edit page instead of opening modal
    if (user.rawUser?._id) {
      navigate(`/dashboard/team/edit/${user.rawUser._id}`);
    } else {
      message.error('User ID not found');
    }
  };

  const handleSaveUser = async (values) => {
    try {
      // Create structured phone number object using form values
      const phoneNumber = values.phoneNumber ? {
        countryCode: values.countryCode || selectedCountryCode || '+27',
        number: values.phoneNumber
      } : null;

      // Update existing user with proper field mapping
      const result = await userService.updateUser(editingUser.id, {
        name: values.name,
        surname: values.surname,
        email: values.email,
        phoneNumber: phoneNumber,
        groupId: groups.find(g => g.groupName === values.role)?._id,
        status: values.status
      });
      
      if (result.success) {
        message.success(`User "${values.name} ${values.surname}" has been updated successfully!`);
        loadUsers(); // Reload the users list
      } else {
        message.error(`Failed to update user: ${result.message}`);
      }
      
      setIsModalVisible(false);
      form.resetFields();
      setSelectedCountryCode('+27'); // Reset country code to default
      setEditingUser(null); // Clear editing user
    } catch (error) {
      console.error('Error updating user:', error);
      message.error('An unexpected error occurred while updating the user. Please try again.');
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedCountryCode('+27');
    setEditingUser(null);
  };

  const handleInviteUser = () => {
    inviteForm.resetFields();
    setIsInviteModalVisible(true);
  };

  const handleSendInvitation = async (values) => {
    setInviteLoading(true);
    try {
      const result = await userService.sendInvitation(values.email, values.groupId);
      
      if (result.success) {
        message.success(`Invitation sent successfully to ${values.email}!`);
        loadUsers(); // Reload the users list
        setIsInviteModalVisible(false);
        inviteForm.resetFields();
      } else {
        // Show generic error message for invitation failures
        if (result.message && (result.message.includes('email') || result.message.includes('phone') || result.message.includes('duplicate') || result.message.includes('exists') || result.message.includes('already'))) {
          message.error(message);
        } else {
          message.error('Failed to send invitation. Please contact your administrator.');
        }
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      message.error('Failed to send invitation. Please contact your administrator.');
    } finally {
      setInviteLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'super admin':
        return 'red';
      case 'admin':
        return 'blue';
      case 'user':
        return 'green';
      default:
        return 'default';
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const filteredUsers = users.filter(user => {
    // Apply search filter
    const matchesSearch = !searchQuery || 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.phoneNumber && user.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Apply participant filter
    const matchesParticipantFilter = participantFilter === 'all' || 
      (participantFilter === 'non-participants' && !user.rawUser?.isParticipant);
    
    return matchesSearch && matchesParticipantFilter;
  });

  const columns = [
    {
      title: 'Full name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar 
            size={40}
            style={{ backgroundColor: '#2E86AB' }}
            icon={record.avatar ? undefined : <UserOutlined />}
            src={record.avatar}
          >
            {!record.avatar && getInitials(text)}
          </Avatar>
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={getRoleColor(role)}>{role}</Tag>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (phone) => phone || 'Not provided',
    },
    {
      title: 'Business Unit',
      dataIndex: 'businessUnit',
      key: 'businessUnit',
      render: (bu) => {
        const name = bu ? (buMap[bu] || bu) : null;
        return name ? name : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (dept) => dept || <Text type="secondary">—</Text>,
    },
    {
      title: 'Privileges',
      dataIndex: 'questionnaires',
      key: 'questionnaires',
      render: (privilegeCount) => (
        <span>{privilegeCount} privileges</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          pending: { color: 'orange', text: 'Pending' },
          active: { color: 'green', text: 'Active' },
          inactive: { color: 'red', text: 'Inactive' }
        };
        const config = statusConfig[status] || statusConfig.active;
        return (
          <Tag color={config.color}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/dashboard/member/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditUser(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <PageHeader 
        title="TapLearnGo Team" 
        subtitle="Manage users and user accounts"
        breadcrumbs={BREADCRUMB_PATTERNS.team}
      />
        <SearchCard
          searchPlaceholder="Search people by name, surname, email, phone..."
          searchValue={searchQuery}
          onSearchChange={(e) => setSearchQuery(e.target.value)}
          onSearchClick={() => {/* Search is handled by filtering */}}
        />
      <Card>
        {/* Search Card */}


        {/* Participant Filter */}
        <div style={{ 
          background: '#fff',
          padding: '16px',
          borderRadius: '8px',
          marginBottom: '16px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ fontWeight: 500, color: '#666' }}>User Type Filter:</div>
            <Radio.Group 
              value={participantFilter} 
              onChange={(e) => setParticipantFilter(e.target.value)}
            >
              <Radio.Button value="non-participants">Non-Participants Only</Radio.Button>
              <Radio.Button value="all">All Users</Radio.Button>
            </Radio.Group>
          </Space>
        </div>

        <div style={{ marginBottom: '24px' }}>
          {/* User count and Invite Button */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px' 
          }}>
            <div style={{ color: '#666' }}>
              <span>
                {filteredUsers.length} {participantFilter === 'non-participants' ? 'non-participant' : ''} user{filteredUsers.length !== 1 ? 's' : ''} found
              </span>
            </div>
            <Button 
              
              icon={<PlusOutlined />} 
              onClick={handleInviteUser}
            >
              Invite User
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <Table
          columns={columns}
          dataSource={filteredUsers}
          loading={loading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} items`,
            pageSize: 10,
          }}
        />

        {/* Add/Edit User Modal */}
        <Modal
          title="Edit User"
          open={isModalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSaveUser}
          >
            <Form.Item
              name="name"
              label="First Name"
              rules={[{ required: true, message: 'Please enter first name' }]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>

            <Form.Item
              name="surname"
              label="Last Name"
              rules={[{ required: true, message: 'Please enter last name' }]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Please enter email' },
                { type: 'email', message: 'Please enter valid email' }
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            {/* Phone Number with Country Code */}
            <Form.Item label="Phone Number" style={{ marginBottom: '16px' }}>
              <Input.Group compact>
                <Form.Item
                  name="countryCode"
                  noStyle
                  rules={[{ required: false }]}
                  initialValue="+27"
                >
                  <Select
                    placeholder="Country"
                    showSearch
                    optionFilterProp="children"
                    style={{ width: '45%', fontSize: 12 }}
                    dropdownStyle={{ minWidth: 280 }}
                    onChange={(value) => {
                      setSelectedCountryCode(value);
                      form.setFieldsValue({ countryCode: value });
                    }}
                  >
                    {countryCodes.map(country => (
                      <Option key={country.code} value={country.dial} style={{ fontSize: 12 }}>
                        {country.name} ({country.dial})
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="phoneNumber"
                  noStyle
                  rules={[
                    { required: false },
                    { 
                      pattern: /^[0-9\s\-\(\)]+$/, 
                      message: 'Please enter a valid phone number' 
                    }
                  ]}
                >
                  <Input 
                    style={{ width: '55%' }}
                    placeholder="Enter phone number"
                    prefix={<PhoneOutlined />}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            <Form.Item
              name="role"
              label="Group/Role"
              rules={[{ required: true, message: 'Please select group' }]}
            >
              <Select placeholder="Select group">
                {groups.map(group => (
                  <Option key={group._id} value={group.groupName}>
                    {group.groupName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="status"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
              initialValue="active"
            >
              <Select placeholder="Select status">
                <Option value="pending">Pending</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Form.Item>

            <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
              <Space>
                <Button onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button  htmlType="submit">
                  {editingUser ? 'Update' : 'Add'} User
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Invite User Modal */}
        <Modal
          title="Invite User"
          open={isInviteModalVisible}
          onCancel={() => !inviteLoading && setIsInviteModalVisible(false)}
          footer={null}
          width={500}
          closable={!inviteLoading}
          maskClosable={!inviteLoading}
        >
          <Form
            form={inviteForm}
            layout="vertical"
            onFinish={handleSendInvitation}
          >
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: 'Please enter email address' },
                { type: 'email', message: 'Please enter a valid email address' }
              ]}
            >
              <Input placeholder="user@example.com" />
            </Form.Item>

            <Form.Item
              label="Group"
              name="groupId"
              rules={[{ required: true, message: 'Please select a group' }]}
            >
              <Select placeholder="Select a group">
                {groups.map(group => (
                  <Option key={group._id || group.id} value={group._id || group.id}>
                    {group.groupName || group.name}
                    {group.status === 'inactive' && <span style={{ color: '#999' }}> (Inactive)</span>}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div style={{ marginTop: '20px', padding: '15px', background: '#f0f8ff', borderRadius: '6px', border: '1px solid #d1ecf1' }}>
              <p style={{ margin: 0, color: '#0c5460', fontSize: '14px' }}>
                <strong>Note:</strong> An invitation email will be sent to the user with instructions to complete their registration. 
                The invitation will expire in 7 days.
              </p>
            </div>

            <Form.Item style={{ marginBottom: 0, marginTop: '20px' }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button 
                  onClick={() => setIsInviteModalVisible(false)}
                  disabled={inviteLoading}
                >
                  Cancel
                </Button>
                <Button 
                   
                  htmlType="submit"
                  loading={inviteLoading}
                >
                  Send Invitation
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default Team;